import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './PromoTrialPopup.css';
import appIcon from '../../assets/logo_white_bg.png';

const APP_FEATURES = [
  'List & manage properties with exact location',
  'Pinpoint coordinates for accurate property mapping',
  'Share property location instantly with buyers',
  'Boost visibility for rent, sale & resale listings',
  'Easy property posting with smart tools',
];

const PromoTrialPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef(null);
  const showDelayRef = useRef(null);
  const SESSION_SHOWN_KEY = '360coordinates_popup_shown';

  const hasBeenShownInSession = () => {
    return sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true';
  };

  const markAsShownInSession = () => {
    sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');
  };

  const shouldShowOnRoute = () => {
    const path = location.pathname.toLowerCase();

    if (path === '/login' || path === '/register') {
      return false;
    }

    // Full download page already shows the same content
    if (path === '/downloadapp') {
      return false;
    }

    if (path.includes('/details/') || path.includes('/viewdetails')) {
      return false;
    }

    // Logged-in dashboard areas (not public marketing pages like /buyer or /seller)
    const dashboardExactPaths = new Set([
      '/buyer-dashboard',
      '/buyerhome',
      '/buy',
      '/rent',
      '/pghostel',
      '/projects',
      '/buyerprofile',
      '/chatus',
      '/chat',
      '/searchresults',
      '/seller-dashboard',
    ]);
    if (dashboardExactPaths.has(path)) {
      return false;
    }

    const dashboardPrefixes = [
      '/seller/properties',
      '/seller/inquiries',
      '/agent/',
      '/upcoming-project/',
    ];
    if (dashboardPrefixes.some((prefix) => path.startsWith(prefix))) {
      return false;
    }

    return true;
  };

  // Manual close handler - just closes without disabling
  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
    }, 250); // Match CSS animation duration
    
    // Clear timeout if still running
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const PLAY_STORE_URL =
    'https://play.google.com/store/apps/details?id=com.indiapropertys.app360';
  const APP_STORE_URL =
    'https://apps.apple.com/in/app/360coordinates/id6768521931';

  const handlePlayStoreClick = () => {
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const handleAppStoreClick = () => {
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  useEffect(() => {
    // Legacy: store buttons used to disable the popup forever — clear so it can show again
    localStorage.removeItem('360coordinates_popup_disabled');

    // Don't show if popup has already been shown in this session
    if (hasBeenShownInSession()) {
      return;
    }

    // Check if should show on current route
    if (!shouldShowOnRoute()) {
      return;
    }

    // Clear any existing timeouts
    if (showDelayRef.current) {
      clearTimeout(showDelayRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Small delay to ensure smooth animation on page load
    showDelayRef.current = setTimeout(() => {
      setIsAnimating(true);
      setIsVisible(true);
      
      // Mark as shown in this session so it won't appear again
      markAsShownInSession();
      
      // Popup will remain visible until user clicks download or close button
    }, 300); // Small delay for smooth page load

    // Cleanup on unmount or route change
    return () => {
      if (showDelayRef.current) {
        clearTimeout(showDelayRef.current);
        showDelayRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [location.pathname]); // Re-check when route changes

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`promo-trial-popup-overlay ${isAnimating ? 'promo-trial-popup-visible' : 'promo-trial-popup-hidden'}`}>
      <div className="promo-trial-popup-container">
        <button
          type="button"
          className="promo-trial-popup-close"
          onClick={handleClose}
          aria-label="Close popup"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <aside className="promo-trial-popup-brand">
          <img className="promo-trial-popup-app-icon" src={appIcon} alt="360Coordinates App" />
          <span className="promo-trial-popup-brand-name">360Coordinates</span>
        </aside>

        <div className="promo-trial-popup-main">
          <h2 className="promo-trial-popup-title">Download the app</h2>
          <p className="promo-trial-popup-subtitle">List, search and share properties with exact map locations</p>

          <ul className="promo-trial-popup-features">
            {APP_FEATURES.map((feature) => (
              <li key={feature} className="promo-trial-popup-feature">
                <span className="promo-trial-popup-feature-icon" aria-hidden>✓</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <div className="promo-trial-popup-store-buttons">
            <button
              type="button"
              className="promo-trial-popup-cta promo-trial-popup-cta--primary"
              onClick={handlePlayStoreClick}
            >
              <svg className="playstore-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 0 1-.609-.92V2.734a.996.996 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-7.134 4.115L7.88 16.42l6.619-5.713zm.805-.707L8.469 5.584l2.114-1.219 7.134 4.115-2.302 2.302zM21.5 11.5c.5.3.5.7 0 1l-3.25 1.875L15.75 12l2.5-2.375 3.25 1.875z" />
              </svg>
              Play Store
            </button>

            <button
              type="button"
              className="promo-trial-popup-cta promo-trial-popup-cta--secondary"
              onClick={handleAppStoreClick}
            >
              <svg className="playstore-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              App Store
            </button>
          </div>

          <p className="promo-trial-popup-limited">No signup required to download</p>
        </div>
      </div>
    </div>
  );
};

export default PromoTrialPopup;